import React, { useState } from 'react';
import { Button, Input } from '../shared';
import type { Client, CreateClientInput } from '../../types';
import styles from './NewClientForm.module.css';

interface NewClientFormProps {
  onSubmit: (input: CreateClientInput) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
}

export const NewClientForm: React.FC<NewClientFormProps> = ({
  onSubmit,
  onCancel,
  isLoading,
}) => {
  const [formData, setFormData] = useState<CreateClientInput>({
    name: '',
    age: 30,
    goal: 'muscle_gain',
    level: 'beginner',
    equipment: 'full_gym',
    daysPerWeek: 3,
    injuries: '',
  });

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    await onSubmit(formData);
  };

  const goals: { value: Client['goal']; label: string }[] = [
    { value: 'muscle_gain', label: 'Muscle Gain' },
    { value: 'fat_loss', label: 'Fat Loss' },
    { value: 'strength', label: 'Strength' },
    { value: 'endurance', label: 'Endurance' },
    { value: 'rehab', label: 'Rehabilitation' },
  ];

  const levels: { value: Client['level']; label: string }[] = [
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'advanced', label: 'Advanced' },
  ];

  const equipment: { value: Client['equipment']; label: string }[] = [
    { value: 'full_gym', label: 'Full Gym' },
    { value: 'home', label: 'Home Gym' },
    { value: 'dumbbells', label: 'Dumbbells Only' },
    { value: 'bodyweight', label: 'Bodyweight Only' },
  ];

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>New Client</h2>
      
      <form onSubmit={handleSubmit} className={styles.form}>
        <Input
          type="text"
          label="Full Name"
          placeholder="John Doe"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          required
          disabled={isLoading}
        />

        <div className={styles.row}>
          <Input
            type="number"
            label="Age"
            value={formData.age}
            onChange={(e) => setFormData(prev => ({ ...prev, age: parseInt(e.target.value) || 0 }))}
            min={10}
            max={120}
            required
            disabled={isLoading}
          />

          <Input
            type="number"
            label="Days/Week"
            value={formData.daysPerWeek}
            onChange={(e) => setFormData(prev => ({ ...prev, daysPerWeek: parseInt(e.target.value) || 0 }))}
            min={1}
            max={7}
            required
            disabled={isLoading}
          />
        </div>

        <div className={styles.selectGroup}>
          <label className={styles.selectLabel}>Goal</label>
          <select
            className={styles.select}
            value={formData.goal}
            onChange={(e) => setFormData(prev => ({ ...prev, goal: e.target.value as Client['goal'] }))}
            disabled={isLoading}
          >
            {goals.map(g => (
              <option key={g.value} value={g.value}>{g.label}</option>
            ))}
          </select>
        </div>

        <div className={styles.selectGroup}>
          <label className={styles.selectLabel}>Experience Level</label>
          <select
            className={styles.select}
            value={formData.level}
            onChange={(e) => setFormData(prev => ({ ...prev, level: e.target.value as Client['level'] }))}
            disabled={isLoading}
          >
            {levels.map(l => (
              <option key={l.value} value={l.value}>{l.label}</option>
            ))}
          </select>
        </div>

        <div className={styles.selectGroup}>
          <label className={styles.selectLabel}>Available Equipment</label>
          <select
            className={styles.select}
            value={formData.equipment}
            onChange={(e) => setFormData(prev => ({ ...prev, equipment: e.target.value as Client['equipment'] }))}
            disabled={isLoading}
          >
            {equipment.map(e => (
              <option key={e.value} value={e.value}>{e.label}</option>
            ))}
          </select>
        </div>

        <div className={styles.textareaGroup}>
          <label className={styles.textareaLabel}>Injuries / Limitations (optional)</label>
          <textarea
            className={styles.textarea}
            value={formData.injuries}
            onChange={(e) => setFormData(prev => ({ ...prev, injuries: e.target.value }))}
            placeholder="e.g., Lower back pain, shoulder impingement..."
            rows={3}
            disabled={isLoading}
          />
        </div>

        <div className={styles.actions}>
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={isLoading}
            disabled={!formData.name || isLoading}
          >
            Create Client
          </Button>
        </div>
      </form>
    </div>
  );
};
